import {
  cloneElement,
  type ReactElement,
  useId,
  useMemo,
  useState,
  useCallback,
  type JSX,
} from 'react';
import {
  DropdownCellOptionType,
  type OptionGroup,
  type Option,
  type DropdownProps,
} from '../../dropdown';
import {
  type SelectChangeEvent,
  type SelectProps as MuiSelectProps,
} from '@mui/material/Select';
import { menuItemClasses } from '@mui/material/MenuItem';
import { paperClasses } from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { type PopoverOrigin } from '@mui/material/Popover';
import { type MenuProps } from '@mui/material/Menu';
import { useTheme } from '../../../../theme';
import {
  StyledSelect,
  VisuallyHidden,
  placeholderClassName,
  placeholderDefaultClassName,
} from './select.styles';
import { Icon, type IconProps } from '@doxyme/icons';
import { useDropdownStyles } from '../../dropdown/dropdown.styles';
import { type CSSObject } from '@emotion/react';
import { Spinner } from '../../spinner';
import { getSelectOptions } from './getSelectOptions';
import { Status } from '../../status';

export type SelectProps = Omit<MuiSelectProps<string>, 'onChange'> & {
  id?: string;
  options: Option[] | OptionGroup[];
  selected?: Option;
  onChange?: (value: Option) => void;
  hasError?: boolean;
  hasErrorAdornment?: boolean;
  isLoading?: boolean;
  overrideDropdownContent?: JSX.Element;
  type?: DropdownCellOptionType;
  dropdownMaxHeight?: DropdownProps['maxHeight'];
  dropdownWidth?: CSSObject['width'];
  transformOrigin?: PopoverOrigin;
  anchorOrigin?: PopoverOrigin;
  menuProps?: Partial<MenuProps>;
  prefix?: string;
  labelWeight?: 'normal' | 'bold';
  fullWidth?: boolean;
};

const isOptionGroup = (option: Option | OptionGroup): option is OptionGroup =>
  'options' in option;

const EMPTY_DROPDOWN_HEIGHT = 152;

/**
 * Select component is a dropdown with a list of options.
 * Note that we're using child components from the `Dropdown` component here to avoid duplication of styles.
 * We cannot use the `Dropdown` component directly because it is not easily supported by the @mui/material library.
 */
export const Select = ({
  options = [],
  selected,
  onChange,
  placeholder = '',
  disabled,
  hasErrorAdornment = false,
  isLoading = false,
  overrideDropdownContent,
  startAdornment,
  labelId,
  'aria-label': ariaLabel,
  type = DropdownCellOptionType.Default,
  dropdownMaxHeight = 320,
  dropdownWidth,
  transformOrigin = {
    vertical: 'top',
    horizontal: 'left',
  },
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  menuProps,
  prefix,
  labelWeight = 'normal',
  fullWidth = true,
  ...rest
}: SelectProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const fallbackLabelId = `select-${useId()}`;

  const dropdownStyles = useDropdownStyles({
    width: dropdownWidth,
    maxHeight: `${String(dropdownMaxHeight)}px!important`,
    padding: 0,
  });

  const flattenedOptions = useMemo(() => {
    return options.flatMap((option) => {
      if (isOptionGroup(option)) {
        return option.options.map((groupOption) => ({
          ...groupOption,
          group: option.title,
        }));
      }
      return [option];
    });
  }, [options]);

  const handleChange = (e: SelectChangeEvent): void => {
    const value = e.target.value;
    const option = flattenedOptions.find((o) => o.value === value) as Option;
    onChange?.(option);
    setIsOpen(false);
  };

  const getRenderedValue = useCallback(
    (value: Option['value']) => {
      const option = flattenedOptions.find((o) => o.value === value);
      return value ? option?.label ?? value : placeholder;
    },
    [flattenedOptions, placeholder],
  );

  return (
    <>
      {!Boolean(labelId) && Boolean(ariaLabel) && (
        <VisuallyHidden id={fallbackLabelId}>{ariaLabel}</VisuallyHidden>
      )}
      <StyledSelect
        labelId={labelId ?? fallbackLabelId}
        displayEmpty
        variant='outlined'
        theme={theme}
        fullWidth={fullWidth}
        value={selected?.value ?? ''}
        open={isOpen}
        disabled={disabled}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onChange={handleChange}
        renderValue={(value) => (
          <span
            className={`${placeholderClassName} ${
              !Boolean(value) || Boolean(disabled)
                ? placeholderDefaultClassName
                : ''
            }`}
          >
            {prefix && <span>{prefix}&nbsp;</span>}
            <span style={{ fontWeight: labelWeight }}>
              {getRenderedValue(value)}
            </span>
          </span>
        )}
        MenuProps={{
          transformOrigin,
          anchorOrigin,
          slotProps: {
            paper: {
              elevation: undefined,
              style: { marginTop: theme.spacing(1) },
            },
            root: {
              sx: {
                [`.${paperClasses.root}`]: {
                  ...dropdownStyles.container,
                  ' > ul': dropdownStyles.menu,
                  [`.${menuItemClasses.root}.Mui-focusVisible`]: {
                    backgroundColor: theme.menu.background.selected,
                  },
                },
              },
            },
          },
          ...menuProps,
        }}
        IconComponent={(props) => (
          <Box
            sx={{
              right: theme.spacing(1),
            }}
          >
            <Icon.ChevronDown size='small' {...props} />
          </Box>
        )}
        className={placeholderClassName}
        startAdornment={
          Boolean(startAdornment) && (
            <Box
              sx={{
                left: 6,
              }}
            >
              {cloneElement(startAdornment as ReactElement<IconProps>, {
                color: Boolean(disabled)
                  ? theme.icon.tertiary
                  : theme.icon.primary,
              })}
            </Box>
          )
        }
        endAdornment={
          hasErrorAdornment && (
            <Status
              statusLevel='error'
              size={20}
              css={{
                right: '48px',
                position: 'absolute',
                top: 'calc(50% - 10px)',
              }}
            />
          )
        }
        {...rest}
      >
        {isLoading ? (
          <>
            <Box
              role='presentation'
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: EMPTY_DROPDOWN_HEIGHT,
              }}
            >
              <Spinner disableShrink={false} mode='green' size={32} />
            </Box>
          </>
        ) : overrideDropdownContent ? (
          <>
            <Box
              sx={{
                color: theme.text.generic.secondary,
                padding: theme.spacing(2),
              }}
            >
              {overrideDropdownContent}
            </Box>
          </>
        ) : (
          getSelectOptions({ options, selected, type, theme })
        )}
      </StyledSelect>
    </>
  );
};
